import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS, type WhatsAppIdentitySlot, type WhatsAppSessionHealth } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useBranchWhatsAppConfigQuery,
  usePairingQuery,
  useSessionActionMutation,
  useSwitchIdentityMutation,
  useWhatsAppSessionQuery,
  type WhatsAppIdentityView,
} from '../api/whatsapp.api';
import { PairingCode } from '../components/PairingCode';

/** Only one state means messages can actually go out; everything else is a reason they cannot. */
const TONE: Record<WhatsAppSessionHealth, 'success' | 'warning' | 'danger' | 'neutral'> = {
  CONNECTED: 'success',
  CONNECTING: 'neutral',
  DEGRADED: 'warning',
  QR_REQUIRED: 'warning',
  DISCONNECTED: 'warning',
  NOT_CONFIGURED: 'neutral',
  LOGGED_OUT: 'danger',
  RESTRICTED: 'danger',
  ERROR: 'danger',
};

/**
 * Which WhatsApp number this branch messages customers from.
 *
 * Reading it needs only `notifications.view` — whether messages can go out is operational
 * information a counter assistant is entitled to see. Every button on the page is branch
 * administration and needs `branches.update`, which is deliberately not a messaging capability:
 * being trusted to message one customer is not being trusted to unlink the account a whole branch
 * speaks through.
 *
 * The screen never shows a credential, and the pairing code it draws exists only for as long as
 * WhatsApp honours it.
 */
export function WhatsAppSettingsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { branches, activeBranch } = useBranch();

  // A single-branch user has nothing to choose, so the one branch is the one shown.
  const branchId = activeBranch?.id ?? (branches.length === 1 ? branches[0]?.id : null) ?? null;
  const mayAdminister = can(PERMISSIONS.BRANCHES_UPDATE);

  const [slot, setSlot] = useState<WhatsAppIdentitySlot>('PRIMARY');
  const [switching, setSwitching] = useState(false);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const config = useBranchWhatsAppConfigQuery(branchId);
  const session = useWhatsAppSessionQuery(branchId, true);
  const view = session.data;
  const shown = slot === 'PRIMARY' ? view?.primary : view?.alternative;

  // The code is fetched only while this slot is actually waiting for one to be scanned.
  const pairing = usePairingQuery(branchId, slot, mayAdminister && shown?.status === 'QR_REQUIRED');

  const act = useSessionActionMutation(branchId);
  const switchIdentity = useSwitchIdentityMutation(branchId);

  const run = (action: 'connect' | 'disconnect' | 'logout' | 'reconnect') => {
    setFailure(null);
    act.mutate(
      { action, slot },
      { onError: (error) => setFailure(apiErrorMessage(error) ?? t('whatsapp.errors.actionFailed')) },
    );
  };

  const confirmSwitch = () => {
    if (!config.data) return;
    setFailure(null);
    switchIdentity.mutate(
      {
        activeIdentity: view?.activeIdentity === 'PRIMARY' ? 'ALTERNATIVE' : 'PRIMARY',
        reason,
        version: config.data.version,
      },
      {
        onSuccess: () => {
          setSwitching(false);
          setReason('');
        },
        onError: (error) => setFailure(apiErrorMessage(error) ?? t('whatsapp.errors.switchFailed')),
      },
    );
  };

  if (!branchId) {
    return (
      <>
        <PageHeader title={t('whatsapp.title')} />
        <Alert tone="info">{t('whatsapp.noBranch')}</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('whatsapp.title')} subtitle={t('whatsapp.subtitle')} />

      {view?.alert.kind === 'PRIMARY_UNAVAILABLE' && view.alert.messageKey ? (
        <Alert tone="warning" className="mb-4">
          <p>{t(view.alert.messageKey)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {view.alert.actions.includes('RECONNECT') ? (
              <Button size="sm" onClick={() => run('reconnect')} disabled={act.isPending}>
                {t('whatsapp.actions.RECONNECT')}
              </Button>
            ) : null}
            {view.alert.actions.includes('REAUTHENTICATE') ? (
              <Button size="sm" onClick={() => run('connect')} disabled={act.isPending}>
                {t('whatsapp.actions.REAUTHENTICATE')}
              </Button>
            ) : null}
            {/* Offered only when a person may make the decision and the other number is genuinely live. */}
            {view.alert.canSwitchToAlternative ? (
              <Button size="sm" variant="secondary" onClick={() => setSwitching(true)}>
                {t('whatsapp.actions.USE_ALTERNATIVE')}
              </Button>
            ) : null}
          </div>
        </Alert>
      ) : null}

      {failure ? (
        <Alert tone="error" className="mb-4">
          {failure}
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('whatsapp.identity.activeIdentity')} />
          <div className="space-y-3 p-4 pt-0">
            <IdentityRow
              label={t('whatsapp.identity.primaryNumber')}
              identity={view?.primary}
              isActive={view?.activeIdentity === 'PRIMARY'}
            />
            <IdentityRow
              label={t('whatsapp.identity.alternativeNumber')}
              identity={view?.alternative}
              isActive={view?.activeIdentity === 'ALTERNATIVE'}
            />
          </div>
        </Card>

        <Card>
          <CardHeader title={t('whatsapp.connection.title')} />
          <div className="space-y-4 p-4 pt-0">
            <div className="flex gap-2">
              {(['PRIMARY', 'ALTERNATIVE'] as const).map((option) => (
                <Button
                  key={option}
                  size="sm"
                  variant={slot === option ? 'primary' : 'secondary'}
                  onClick={() => setSlot(option)}
                >
                  {t(`whatsapp.identity.${option}`)}
                </Button>
              ))}
            </div>

            {shown?.configured === false ? (
              <Alert tone="info">{t('whatsapp.identity.notConfigured')}</Alert>
            ) : null}

            {pairing.data?.qr ? <PairingCode value={pairing.data.qr} /> : null}

            {shown?.status === 'QR_REQUIRED' && !pairing.data?.qr ? (
              <Alert tone="info">{t('whatsapp.pairing.waiting')}</Alert>
            ) : null}

            {mayAdminister ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => run('connect')} disabled={act.isPending || !shown?.configured}>
                  {t('whatsapp.connection.connect')}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => run('disconnect')}
                  disabled={act.isPending || !shown?.configured}
                >
                  {t('whatsapp.connection.disconnect')}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => run('logout')}
                  disabled={act.isPending || !shown?.configured}
                >
                  {t('whatsapp.connection.logout')}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('whatsapp.connection.readOnly')}</p>
            )}
          </div>
        </Card>
      </div>

      <Modal
        isOpen={switching}
        onClose={() => setSwitching(false)}
        title={t('whatsapp.switch.title')}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('whatsapp.switch.explain')}</p>
          <Input
            label={t('whatsapp.switch.reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={200}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSwitching(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirmSwitch} disabled={reason.trim().length === 0 || switchIdentity.isPending}>
              {t('whatsapp.switch.confirm')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function IdentityRow({
  label,
  identity,
  isActive,
}: {
  label: string;
  identity: WhatsAppIdentityView | undefined;
  isActive: boolean;
}) {
  const { t } = useTranslation();
  const status = identity?.status ?? 'NOT_CONFIGURED';

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {/* Numbers stay left-to-right inside an Arabic sentence. */}
        <p className="truncate text-sm text-muted-foreground" dir="ltr">
          {identity?.phoneNumber ?? t('whatsapp.identity.notConfigured')}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isActive ? <Badge tone="brand">{t('whatsapp.identity.active')}</Badge> : null}
        <Badge tone={TONE[status]}>{t(`whatsapp.status.${status}`)}</Badge>
      </div>
    </div>
  );
}
